import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomCheckbox from "../../customComponent/CustomCheckbox";
import Reorder from "../../HOME/HomePageComponents/Reorder";
import { useDispatch } from "react-redux";
import { paidPaymentUpdationAction } from "../../../actions/reportActions/reportsActions";

function FinalChoicesList({
  rows,
  columns,
  showStudentNameColumn,
  showStudentIdColumn,
  showStudentUniversityColumn,
  showStudentCourseColumn,
  showStartDateColumn,
  showTutionFeeColumn,
  showInvoiceColumn,
  showPaidToGslColumn,
  showPaidToPartnerColumn,
  showPaymentDateColumn
}) {
  const dispatch = useDispatch();
  const [localState, setLocalState] = useState([]);
  // loading state keyed by row id and field name
  const [loadingState, setLoadingState] = useState({});
  const [inputValues, setInputValues] = useState({});

  // Optional: error state for each update
  const [errorState, setErrorState] = useState({});

  useEffect(() => {
    setLocalState(rows);
  }, [rows]);

  // Helper to update loading state for a specific row field
  const setFieldLoading = (id, field, isLoading) => {
    setLoadingState((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: isLoading }
    }));
  };

  const handleInputChange = (e, rowId) => {
    // Update local input state immediately
    const { value } = e.target;
    setInputValues((prev) => ({ ...prev, [rowId]: value }));
  };

  const handleInputBlur = async (rowId) => {
    // Dispatch action when input loses focus
    const newValue = inputValues[rowId];
    try {
      await dispatch(
        paidPaymentUpdationAction(newValue, "paidFeeCommAmt", rowId)
      );
    } catch (error) {
      // Optionally handle error (show notification, revert value, etc.)
      console.error("Error updating paidFeeCommissionAmt", error);
    }
  };

  // Handler that dispatches the update and waits for it
  const handleCheckboxChange = async (checked, field, id) => {
    setFieldLoading(id, field, true);
    setErrorState((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: null }
    }));
    try {
      // Await the action; assuming it returns a promise and on success,
      // the global rows get updated from the backend
      await dispatch(paidPaymentUpdationAction(checked, field, id));
      // Optionally, update local state if you want to reflect the change immediately:
      setLocalState((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, [fieldMapping(field)]: checked } : row
        )
      );
    } catch (error) {
      // Optionally, show an error message and revert state if needed
      setErrorState((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: error.message || "Update failed" }
      }));
    } finally {
      setFieldLoading(id, field, false);
    }
  };

  // For mapping API field keys to local state keys if they differ.
  // For example, if field "noComm" corresponds to row property "NoCommission", etc.
  const fieldMapping = (field) => {
    const map = {
      invoice: "invoice",
      noComm: "NoCommission",
      commOnGross: "CommissionOnGross",
      commOnNet: "CommissionOnNet",
      paidFeeComm: "paidFeeCommission",
      paidFeeCommAmt: "paidFeeCommissionAmt",
      paidToGsl: "paidToGsl",
      paidToPartner: "paidToPartner"
    };
    return map[field] || field;
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "scroll",
        "&::-webkit-scrollbar": { display: "none" }
      }}
    >
      <Table
        sx={{
          minWidth: "1500px",
          "& .MuiTableCell-root": {
            borderBottom: "1px solid rgba(220, 223, 227, 0.50)"
          }
        }}
      >
        <TableHead
          sx={{
            background: "#F9F9FB",
            "& th": {
              textTransform: "uppercase",
              fontWeight: "600",
              fontFamily: "Inter",
              color: "#666E7D"
            }
          }}
        >
          <TableRow>
            {columns?.map((item, index) => {
              if (item.hidden) return null;
              return (
                <TableCell key={index}>
                  {item.title} <Reorder />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            background: "#fff",
            border: "1px solid rgba(220, 223, 227, 0.50)"
          }}
        >
          {localState?.map((row) => (
            <TableRow key={row.id}>
              {!showStudentIdColumn && (
                <TableCell>{row.applicationId}</TableCell>
              )}
              {!showStudentNameColumn && <TableCell>{row.name}</TableCell>}
              {!showStudentUniversityColumn && (
                <TableCell>{row.university}</TableCell>
              )}
              {!showStudentCourseColumn && <TableCell>{row.course}</TableCell>}
              {!showStartDateColumn && <TableCell>{row.startDate}</TableCell>}
              {!showInvoiceColumn && (
                <TableCell>
                  <CustomCheckbox
                    checked={row.invoice}
                    disabled={
                      loadingState[row.id]?.[
                        "invoice"
                      ] /* disable when loading */
                    }
                    onChange={(e) =>
                      handleCheckboxChange(e.target.checked, "invoice", row.id)
                    }
                  />
                </TableCell>
              )}

              <TableCell>
                <CustomCheckbox
                  checked={row.NoCommission}
                  disabled={loadingState[row.id]?.["noComm"]}
                  onChange={(e) =>
                    handleCheckboxChange(e.target.checked, "noComm", row.id)
                  }
                />
              </TableCell>

              <TableCell>
                <CustomCheckbox
                  checked={row.CommissionOnGross}
                  disabled={loadingState[row.id]?.["commOnGross"]}
                  onChange={(e) =>
                    handleCheckboxChange(
                      e.target.checked,
                      "commOnGross",
                      row.id
                    )
                  }
                />
              </TableCell>

              <TableCell>
                <CustomCheckbox
                  checked={row.CommissionOnNet}
                  disabled={loadingState[row.id]?.["commOnNet"]}
                  onChange={(e) =>
                    handleCheckboxChange(e.target.checked, "commOnNet", row.id)
                  }
                />
              </TableCell>

              <TableCell>
                <CustomCheckbox
                  checked={row.paidFeeCommission}
                  disabled={loadingState[row.id]?.["paidFeeComm"]}
                  onChange={(e) =>
                    handleCheckboxChange(
                      e.target.checked,
                      "paidFeeComm",
                      row.id
                    )
                  }
                />
              </TableCell>

              <TableCell>
                {row.paidFeeCommission && (
                  <input
                    type="number"
                    value={inputValues[row.id] || ""}
                    disabled={!row.paidFeeCommission}
                    onChange={(e) => handleInputChange(e, row.id)}
                    onBlur={() => handleInputBlur(row.id)}
                    style={{
                      width: "100%",
                      padding: "5px",
                      border: "1px solid #ccc",
                      borderRadius: "4px"
                    }}
                  />
                )}
              </TableCell>

              {!showPaidToGslColumn && (
                <TableCell>
                  <CustomCheckbox
                    checked={row.paidToGsl}
                    disabled={loadingState[row.id]?.["paidToGsl"]}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e.target.checked,
                        "paidToGsl",
                        row.id
                      )
                    }
                  />
                </TableCell>
              )}
              {!showPaidToPartnerColumn && (
                <TableCell>
                  <CustomCheckbox
                    checked={row.paidToPartner}
                    disabled={loadingState[row.id]?.["paidToPartner"]}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e.target.checked,
                        "paidToPartner",
                        row.id
                      )
                    }
                  />
                </TableCell>
              )}
              {!showPaymentDateColumn && (
                <TableCell>{row.paymentDate}</TableCell>
              )}
              {/* Optionally, show a loader or error message per row/field */}
              {errorState[row.id] &&
                Object.values(errorState[row.id]).map((errMsg, idx) =>
                  errMsg ? (
                    <TableCell
                      key={idx}
                      sx={{ color: "red", fontSize: "0.75rem" }}
                    >
                      {errMsg}
                    </TableCell>
                  ) : null
                )}
              {/* {loadingState[row.id] &&
                Object.values(loadingState[row.id]).some((v) => v) && (
                  <TableCell>
                    <CircularProgress size={20} />
                  </TableCell>
                )} */}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default FinalChoicesList;
